import React, { useRef, useState } from 'react';
import { BaseFileUpload, CombinedRefContent } from '../FileUpload/BaseFileUpload';
import { ImageUploadParam } from '../../types/ImageUploadParam';
import { ImageFileUploadParam } from '../../types/FileUploadParam';
import { HiddenParam } from '../../types/HiddenParam';
import { NumberParam } from '../../types/NumberParam';
import { formParamsUtils } from '../../utils/formParamsUtils';
import { HiddenInput } from '../HiddenInput/HiddenInput';
import { SelectedImage } from '../../../FileUpload';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import {
  ImagePreview,
  ImageUploadDescription,
  ImageUploadHeadline,
  PreviewSuspense,
} from '../../../components/ImageUpload';
import { isValid, useValidateImage } from '../../../FileUpload/utils/validateImage';
import { ImageCropperModifier } from '../../../../components/molecules/ImageCropper/ImageCropper.modifier';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useValdidationRules } from '../../../FileUpload/utils/useValdidationRules';
import { LazyImageCropper } from '../../../../components/molecules/ImageCropper/LazyImageCropper';
import { ImageContainer } from '../../../components/ImageUpload/ImageContainer';
import IConstraints from '../../../../types/IContraints';
import { Image } from '../../../../types/api/ImageType';

export type ImageUploadWithCroppingParam = ImageUploadParam<
  'imageuploadparams',
  | ImageFileUploadParam<'image'>
  | HiddenParam<'imageType'>
  | HiddenParam<'itemId'>
  | NumberParam<'width'>
  | NumberParam<'height'>
  | NumberParam<'y'>
  | NumberParam<'x'>
>;

export type ImageUploadWithCroppingProps = {
  param: ImageUploadWithCroppingParam;
};

export function ImageUploadWithCropping({ param }: ImageUploadWithCroppingProps) {
  const { t } = useSonTranslation('ImageUpload');

  const params = formParamsUtils(param);
  const image = params.getByName('image');
  const imageType = params.getByName('imageType');
  const itemId = params.findByName('itemId');

  const [selectedImage, setSelectedImage] = useState<SelectedImage | undefined>(undefined);
  const [backendImage] = useState<typeof image.value | undefined>(image.value);
  const [readingFile, setReadingFile] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(undefined);
  const labelRef = useRef<HTMLLabelElement>(undefined);
  const combinedRef = useRef<CombinedRefContent>({
    inputRef: inputRef,
    labelRef: labelRef,
  });
  const getImageCropperData = useRef(undefined);

  const validationRules = useValdidationRules(image.constraints || {});
  const validate = useValidateImage();
  const previewSuspenseId = useHtmlValidId();

  return (
    <>
      <ImageUploadHeadline headlineHierarchy={4} />
      <ImageUploadDescription constraints={image.constraints as IConstraints} />
      <BaseFileUpload
        ref={combinedRef as any} // Typescript misinterprets the CombinedRefContent
        param={image}
        selectButtonLabel={t('ImageUpload:buttons.chooseFile')}
        onSelectedFilesChanged={(event) => {
          const file = event?.target?.files?.item(0);

          if (file) {
            validate(file, validationRules)
              .then(value => setSelectedImage(value))
              .catch(() => setSelectedImage(undefined))
              .finally(() => setReadingFile(false));
          }

          labelRef.current?.focus();
          setReadingFile(true);
        }}
      >
        {selectedImage || backendImage
          ? (
              <PreviewSuspense id={previewSuspenseId} isLoading={readingFile} selectedPreview={selectedImage}>
                {isValid(selectedImage)
                  ? (
                      <ImageContainer filename={selectedImage.file.name}>
                        <LazyImageCropper
                          src={selectedImage.image.src}
                          showZoomer
                          viewportSwitch={false}
                          minimumCropWidth={image.constraints?.minWidth}
                          minimumCropHeight={image.constraints?.minHeight}
                          aspectRatio={image.constraints?.aspectRatio}
                          validationCallback={() => []}
                          registerSubmitAction={(getResultData: undefined) => {
                            getImageCropperData.current = getResultData;
                          }}
                          alt={t('previewImageCropper')}
                          modifier={ImageCropperModifier.FIXED_SQUARE_60_60}
                        />
                      </ImageContainer>
                    )
                  : (
                      <ImagePreview image={{ ...(backendImage as any), copyright: undefined } as Image} />
                    )}
              </PreviewSuspense>
            )
          : null}
      </BaseFileUpload>
      {isValid(selectedImage) ? <HiddenInput {...imageType} /> : null}
      {isValid(selectedImage) && itemId ? <HiddenInput {...itemId} /> : null}
    </>
  );
}
